import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

/*
 * Live FX quote proxy.
 *
 * The browser must never call `api.nsave.com` directly: the upstream only
 * allows `nsave.com` origins, so a direct client fetch is blocked by CORS.
 * Instead this `createServerFn` runs on our own server (Nitro), fetches the
 * upstream server-to-server (CORS does not apply), and returns the normalized
 * quote to the client — mirroring the arrow-dev proxy pattern
 * (`NEXT_PUBLIC_API_URL=.../api` + rewrite -> browser hits its own origin).
 *
 * Upstream (public, no auth — a `@Public()` route):
 *   GET `${FX_API_BASE_URL}/transactions/fx/rate/public`
 *       ?sourceCurrency=USD&targetCurrency=EGP&amount=1000&pricingTier=PRO
 *   -> { exchangeRate: string, fee: string|number, amount: string }
 *
 * Base URL is configurable via `FX_API_BASE_URL` (default `https://api.nsave.com`),
 * so dev can point at `https://api.dev.nsave.com`.
 */

// Server-only runtime env (Nitro). Declared locally so we don't need @types/node.
declare const process: { env: Record<string, string | undefined> };

/** Supported send currencies (guards the proxy against arbitrary passthrough). */
export const SEND_CURRENCIES = ['USD', 'EUR', 'GBP'] as const;
const SendCurrencySchema = z.enum(SEND_CURRENCIES);
export type SendCurrency = z.infer<typeof SendCurrencySchema>;

/** Default/fallback send currency. */
export const SOURCE_CURRENCY: SendCurrency = 'USD';

/**
 * Supported payout currencies (guards the proxy against arbitrary passthrough).
 *
 * SYP is deliberately absent. The upstream does not quote it — it answers
 * `USD -> SYP` with `400 {"message":"SYP transfers are not currently available.
 * You can send USD instead"}` — so every surface here would fall back to a
 * constant we made up ourselves and present it as a rate. A fallback exists to
 * survive a transient upstream blip on a corridor that does work, not to invent
 * a number for one that does not; for a currency with a parallel market rate and
 * a January 2026 redenomination, publishing a guess is worse than omitting it.
 *
 * To enable: confirm the endpoint returns 200 for SYP, then add it here and to
 * `FALLBACK_RATES` with a real observed rate, add the `usd-syp` entry back to
 * `content/calculatorPairs.ts`, and add `/calculator/usd-syp` to
 * `staticPrerenderPaths` in `vite.config.ts`. The Syrian pound already has its
 * display name in `content/ui.ts` + `content/ar/ui.ts` and its flag mapping in
 * `lib/currency-flag.ts`, and the corridor pages pick it up on their own via
 * `preferredReceiveCurrency` below, so nothing else is needed.
 */
export const RECEIVE_CURRENCIES = ['EGP', 'BDT', 'PKR', 'NGN', 'MAD', 'TRY'] as const;
const ReceiveCurrencySchema = z.enum(RECEIVE_CURRENCIES);
export type ReceiveCurrency = z.infer<typeof ReceiveCurrencySchema>;

/**
 * The payout currency a corridor page *wants* to open on, falling back when the
 * upstream does not quote it yet.
 *
 * A corridor landing page should default to its own currency — /send-money-to-syria
 * to SYP — but must not offer one the upstream refuses, or the calculator opens on
 * a pair it cannot price. Rather than hardcoding the fallback in the page and
 * leaving a "switch this when the rate lands" comment for someone to miss, the
 * page states its preference and this resolves it against what is actually
 * supported. Adding SYP to `RECEIVE_CURRENCIES` is then the only edit needed to
 * make the Syria pages open on SYP.
 *
 * `preferred` is a plain `string` on purpose: the whole point is that it may name
 * a currency not (yet) in `ReceiveCurrency`, which is exactly what the union
 * forbids — hence the one cast, guarded by the membership check right before it.
 */
export function preferredReceiveCurrency(
  preferred: string,
  fallback: ReceiveCurrency,
): ReceiveCurrency {
  const supported: readonly string[] = RECEIVE_CURRENCIES;
  return supported.includes(preferred) ? (preferred as ReceiveCurrency) : fallback;
}

/**
 * Static PRO-tier per-unit rates (1 USD -> N target). Client-safe fallback used
 * by the calculator and the rates strip whenever the live upstream is
 * unreachable, so neither ever shows a broken/empty state. Every key here must
 * be a corridor the upstream really quotes — see `RECEIVE_CURRENCIES`.
 */
export const FALLBACK_RATES: Record<ReceiveCurrency, number> = {
  EGP: 49.47,
  BDT: 119.6,
  PKR: 278.4,
  NGN: 1533.2,
  MAD: 9.95,
  TRY: 34.2,
};

export const PRICING_TIERS = ['PRO', 'FREE'] as const;
const PricingTierSchema = z.enum(PRICING_TIERS);
export type PricingTier = z.infer<typeof PricingTierSchema>;

const DEFAULT_BASE_URL = 'https://api.nsave.com';
/** Clamp the amount to a sane server-side range regardless of client input. */
const MAX_AMOUNT = 1_000_000;

/**
 * A finite number, coerced from a `number` or numeric `string`. Deliberately
 * *not* `z.coerce.number()` directly on `unknown` input: JS coerces
 * `Number(null)`/`Number(false)`/`Number([])` to `0`, so a bare `z.coerce
 * .number()` would silently accept `null`/`false`/`[]` as a valid `0` instead
 * of rejecting them. Requiring the raw value to already be a number or
 * string first closes that hole.
 */
const CoercibleFiniteNumber = z
  .union([z.number(), z.string()])
  .transform((v) => Number(v))
  .pipe(z.number().finite());

/**
 * Request contract for `getQuote`/`fetchQuoteUpstream` — the single source of
 * truth for what a caller may send in and what comes back out, so nothing
 * downstream has to guess the shape. `sourceCurrency`/`pricingTier` fall back
 * to their default on anything unrecognized (matching the old ad hoc
 * "coerce sloppy input" behavior); `targetCurrency` has no fallback — an
 * unsupported payout currency is a real caller error and should throw.
 */
const QuoteInputSchema = z.object({
  sourceCurrency: z
    .string()
    .optional()
    .transform((v) => (v ?? SOURCE_CURRENCY).toUpperCase())
    .pipe(SendCurrencySchema)
    .catch(SOURCE_CURRENCY),
  targetCurrency: z
    .string()
    .transform((v) => v.toUpperCase())
    .pipe(ReceiveCurrencySchema),
  amount: CoercibleFiniteNumber.pipe(z.number().positive()).transform((n) =>
    Math.min(n, MAX_AMOUNT),
  ),
  pricingTier: z
    .string()
    .optional()
    .transform((v) => (v ?? 'PRO').toUpperCase())
    .pipe(PricingTierSchema)
    .catch('PRO'),
});

export type QuoteInput = z.input<typeof QuoteInputSchema>;

/**
 * Response contract for the normalized quote returned to the client. The
 * upstream's own response shape is validated separately by
 * `FxRateResponseSchema` below — this is what `getQuote` hands back after
 * normalizing it.
 */
const QuoteSchema = z.object({
  rate: z.number().finite(),
  fee: z.number().finite(),
  amount: z.number().finite(),
});

export type Quote = z.infer<typeof QuoteSchema>;

/**
 * Upstream response shape for `GET .../transactions/fx/rate/public` — see the
 * module doc comment above. Fields arrive as either `string` or `number`
 * depending on the upstream's own formatting, hence `CoercibleFiniteNumber`.
 */
const FxRateResponseSchema = z.object({
  exchangeRate: CoercibleFiniteNumber,
  fee: CoercibleFiniteNumber,
  amount: CoercibleFiniteNumber,
});

/**
 * Server-to-server fetch of a single live quote from the nsave backend.
 * Throws on network error, non-200, or a malformed body. Shared by both
 * `getQuote` (single) and `getRates` (batch).
 */
async function fetchQuoteUpstream(
  sourceCurrency: SendCurrency,
  targetCurrency: ReceiveCurrency,
  amount: number,
  pricingTier: PricingTier,
): Promise<Quote> {
  const baseUrl = process.env.FX_API_BASE_URL || DEFAULT_BASE_URL;
  const url = new URL('/transactions/fx/rate/public', baseUrl);
  url.searchParams.set('sourceCurrency', sourceCurrency);
  url.searchParams.set('targetCurrency', targetCurrency);
  url.searchParams.set('amount', String(amount));
  url.searchParams.set('pricingTier', pricingTier);

  let response: Response;
  try {
    response = await fetch(url, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    });
  } catch (cause) {
    throw new Error(`FX upstream unreachable: ${String(cause)}`, { cause });
  }

  if (!response.ok) {
    throw new Error(`FX upstream error: ${response.status} ${response.statusText}`);
  }

  const parsed = FxRateResponseSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new Error(`FX upstream returned a malformed quote: ${parsed.error.message}`);
  }

  return QuoteSchema.parse({
    rate: parsed.data.exchangeRate,
    fee: parsed.data.fee,
    amount: parsed.data.amount,
  });
}

/**
 * Fetch a live FX quote from the nsave backend. Runs server-side only.
 * Throws on invalid input or upstream failure; callers fall back to their own
 * static estimate so the UI never shows a broken state.
 */
export const getQuote = createServerFn({ method: 'GET' })
  .validator((input: QuoteInput) => QuoteInputSchema.parse(input))
  .handler(async ({ data }): Promise<Quote> => {
    const { sourceCurrency, targetCurrency, amount, pricingTier } = data;
    return fetchQuoteUpstream(sourceCurrency, targetCurrency, amount, pricingTier);
  });

export type RateEntry = { code: ReceiveCurrency; rate: number };

const RatesInputSchema = z.object({
  pricingTier: z
    .string()
    .optional()
    .transform((v) => (v ?? 'PRO').toUpperCase())
    .pipe(PricingTierSchema)
    .catch('PRO'),
});

export type RatesInput = z.input<typeof RatesInputSchema>;

/**
 * Batch-fetch the live per-unit rate (1 USD -> N target) for every supported
 * payout currency in parallel, in a single client round-trip. Runs server-side.
 * Honours the pricing tier so the strip reflects the calculator's selected plan.
 * Pairs whose upstream call fails are omitted; the client falls back to
 * `FALLBACK_RATES` for anything missing so the strip always renders all pairs.
 */
export const getRates = createServerFn({ method: 'GET' })
  .validator((input?: RatesInput) => RatesInputSchema.parse(input ?? {}))
  .handler(async ({ data }): Promise<RateEntry[]> => {
    const { pricingTier } = data;
    const settled = await Promise.allSettled(
      RECEIVE_CURRENCIES.map(async (code): Promise<RateEntry> => {
        const quote = await fetchQuoteUpstream(SOURCE_CURRENCY, code, 1000, pricingTier);
        return { code, rate: quote.rate };
      }),
    );

    return settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
  });
